/**
 * Core Nexus Product Page — JavaScript
 * v4 — AJAX add-to-cart, variant sync across all buttons
 * 
 * Handles:
 * - Sticky buy bar visibility (IntersectionObserver + scroll fallback)
 * - FAQ accordion open/close
 * - Product gallery thumbnail switching
 * - Scroll-triggered fade-in animations
 * - Variant color selector (syncs ALL buttons: main, sticky, bundle)
 * - AJAX add to cart (no page redirect)
 * - Bundle add to cart
 * - Video play interaction
 *
 * v4 changes (Mar 5, 2026):
 * - Add to Cart converted from form POST to AJAX (stays on page)
 * - Sticky bar converted to AJAX
 * - Color selector now syncs sticky bar + bundle button variant IDs
 * - Shared updateCartBubble() for consistent cart count updates
 */

(function() {
  'use strict';

  /* ===== Mark JS as ready (enables CSS animations) ===== */
  document.documentElement.classList.add('cn-js');

  /* ===== Sticky Buy Bar — ROBUST implementation ===== */
  function initStickyBuyBar() {
    var bar = document.querySelector('[data-cn-sticky-bar]');
    if (!bar) return;

    // Move bar to document.body to escape any overflow:hidden ancestors
    if (bar.parentNode !== document.body) {
      document.body.appendChild(bar);
    }

    // Force bar styles to ensure visibility when class is added
    bar.style.position = 'fixed';
    bar.style.bottom = '0';
    bar.style.left = '0';
    bar.style.right = '0';
    bar.style.zIndex = '2147483647';

    // Find the hero section or the buy button area
    var hero = document.querySelector('[data-cn-hero]');
    var buyBtn = document.querySelector('[data-cn-add-to-cart]');
    var observeTarget = hero || buyBtn;

    if (!observeTarget) {
      // No hero found — show bar after scrolling 600px
      var scrollFallback = function() {
        var scrollY = window.pageYOffset || document.documentElement.scrollTop;
        if (scrollY > 600) {
          bar.classList.add('visible');
        } else {
          bar.classList.remove('visible');
        }
      };
      window.addEventListener('scroll', scrollFallback, { passive: true });
      scrollFallback();
      return;
    }

    // Use IntersectionObserver if available
    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (!entry.isIntersecting) {
            bar.classList.add('visible');
          } else {
            bar.classList.remove('visible');
          }
        });
      }, { threshold: 0, rootMargin: '0px' });

      observer.observe(observeTarget);
    } else {
      // Fallback for older browsers
      var checkScroll = function() {
        var rect = observeTarget.getBoundingClientRect();
        if (rect.bottom < 0) {
          bar.classList.add('visible');
        } else {
          bar.classList.remove('visible');
        }
      };
      window.addEventListener('scroll', checkScroll, { passive: true });
      checkScroll();
    }

    // Safety check: after 2 seconds, verify bar is appended to body
    setTimeout(function() {
      if (bar.parentNode !== document.body) {
        document.body.appendChild(bar);
      }
    }, 2000);
  }

  /* ===== Fade-in on scroll ===== */
  function initFadeIn() {
    var els = document.querySelectorAll('.cn-fade-in');
    if (!els.length) return;

    if (!('IntersectionObserver' in window)) {
      els.forEach(function(el) { el.classList.add('visible'); });
      return;
    }

    var obs = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          obs.unobserve(entry.target);
        }
      });
    }, { threshold: 0.05, rootMargin: '0px 0px -20px 0px' });

    els.forEach(function(el) { obs.observe(el); });

    // Force-check elements already in viewport
    requestAnimationFrame(function() {
      els.forEach(function(el) {
        var rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom > 0) {
          el.classList.add('visible');
        }
      });
    });
  }

  /* ===== FAQ Accordion ===== */
  function initFaqAccordion() {
    var questions = document.querySelectorAll('[data-cn-faq-toggle]');
    if (!questions.length) return;

    questions.forEach(function(btn) {
      btn.addEventListener('click', function() {
        var item = btn.closest('.cn-faq__item');
        var answer = item.querySelector('.cn-faq__answer');
        var isOpen = item.classList.contains('open');

        document.querySelectorAll('.cn-faq__item.open').forEach(function(openItem) {
          openItem.classList.remove('open');
          var openAnswer = openItem.querySelector('.cn-faq__answer');
          if (openAnswer) openAnswer.style.maxHeight = '0';
        });

        if (!isOpen) {
          item.classList.add('open');
          answer.style.maxHeight = answer.scrollHeight + 'px';
        }
      });
    });
  }

  /* ===== Gallery Thumbnails (Desktop — legacy, kept for fallback) ===== */
  function initGallery() {
    // Thumbnails now synced via initCarousel — this is a no-op
  }

  /* ===== Gallery Carousel + Thumbnail Sync ===== */
  function initCarousel() {
    var carousel = document.querySelector('[data-cn-gallery-carousel]');
    var thumbs = document.querySelectorAll('[data-cn-thumb]');
    var slides = document.querySelectorAll('[data-cn-slide]');
    var prevBtn = document.querySelector('[data-cn-arrow-prev]');
    var nextBtn = document.querySelector('[data-cn-arrow-next]');
    if (!carousel || slides.length < 2) return;

    var scrollTimer = null;
    var totalSlides = slides.length;

    // Get current slide index from scroll position
    function getCurrentIndex() {
      var slideWidth = carousel.offsetWidth;
      if (slideWidth === 0) return 0;
      return Math.round(carousel.scrollLeft / slideWidth);
    }

    // Update active thumbnail based on current slide
    function updateThumbs() {
      var index = getCurrentIndex();
      thumbs.forEach(function(thumb, i) {
        thumb.classList.toggle('active', i === index);
      });
      // Scroll the active thumb into view if needed
      if (thumbs[index]) {
        // Keep thumbnail synchronization inside its horizontal strip.
        // scrollIntoView also moved the whole page and could override the demo link.
        var thumb = thumbs[index];
        var strip = thumb.parentElement;
        var thumbBox = thumb.getBoundingClientRect();
        var stripBox = strip.getBoundingClientRect();
        strip.scrollTo({ left: strip.scrollLeft + thumbBox.left - stripBox.left - (strip.clientWidth - thumbBox.width) / 2, behavior: 'smooth' });
      }
    }

    // Scroll to a specific slide index
    function goToSlide(index) {
      if (index < 0) index = 0;
      if (index >= totalSlides) index = totalSlides - 1;
      var slideWidth = carousel.offsetWidth;
      carousel.scrollTo({ left: index * slideWidth, behavior: 'smooth' });
    }

    // Listen for scroll (fires during swipe or arrow click)
    carousel.addEventListener('scroll', function() {
      if (scrollTimer) clearTimeout(scrollTimer);
      scrollTimer = setTimeout(updateThumbs, 50);
    }, { passive: true });

    // Thumbnail click → scroll carousel to that slide
    thumbs.forEach(function(thumb, i) {
      thumb.addEventListener('click', function() {
        goToSlide(i);
        thumbs.forEach(function(t) { t.classList.remove('active'); });
        thumb.classList.add('active');
      });
    });

    // Arrow click → prev/next slide
    if (prevBtn) {
      prevBtn.addEventListener('click', function(e) {
        e.preventDefault();
        goToSlide(getCurrentIndex() - 1);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function(e) {
        e.preventDefault();
        goToSlide(getCurrentIndex() + 1);
      });
    }
  }

  /* ===== Color/Variant Selector — syncs ALL buttons ===== */
  function initVariantSelector() {
    var options = document.querySelectorAll('[data-cn-color-option]');
    if (!options.length) return;

    options.forEach(function(opt) {
      opt.addEventListener('click', function() {
        options.forEach(function(o) { o.classList.remove('active'); });
        opt.classList.add('active');

        var selector = opt.closest('.cn-variant-selector');
        var label = selector ? selector.querySelector('[data-cn-color-name]') : null;
        var name = opt.getAttribute('data-color-name');
        if (label && name) {
          label.textContent = name;
        }

        var variantId = opt.getAttribute('data-variant-id');
        if (variantId) {
          // Update main product form
          var forms = document.querySelectorAll('#cn-product-form input[name="id"]');
          forms.forEach(function(input) { input.value = variantId; });

          // Sync sticky bar variant
          var stickyInput = document.querySelector('#cn-sticky-bar input[name="id"]');
          if (stickyInput) {
            stickyInput.value = variantId;
          }

          // Sync bundle button variant
          var bundleBtn = document.querySelector('[data-cn-bundle-add]');
          if (bundleBtn) {
            bundleBtn.setAttribute('data-variant-id', variantId);
          }
        }
      });
    });
  }

  /* ===== Update Cart Bubble — fetches real cart count from Shopify ===== */
  function updateCartBubble() {
    fetch('/cart.js')
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        // Dawn theme cart count bubbles
        var bubbles = document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]');
        bubbles.forEach(function(b) { b.textContent = cart.item_count; });
        // Accessibility text
        var srBubbles = document.querySelectorAll('.cart-count-bubble .visually-hidden');
        srBubbles.forEach(function(b) { b.textContent = cart.item_count + ' item' + (cart.item_count !== 1 ? 's' : ''); });
        // Show bubble if hidden
        var containers = document.querySelectorAll('.cart-count-bubble');
        containers.forEach(function(c) {
          if (cart.item_count > 0) c.removeAttribute('hidden');
        });
      })
      .catch(function() {});
  }

  /* ===== Add to Cart — AJAX (stays on page, no redirect) ===== */
  function initAddToCart() {
    var form = document.getElementById('cn-product-form');
    if (!form) return;

    form.addEventListener('submit', function(e) {
      e.preventDefault();

      var btn = form.querySelector('[data-cn-add-to-cart]');
      var variantInput = form.querySelector('input[name="id"]');
      var quantityInput = form.querySelector('input[name="quantity"]');
      if (!btn || !variantInput) return;

      var variantId = variantInput.value;
      var quantity = quantityInput ? (quantityInput.value || 1) : 1;
      var originalHTML = btn.innerHTML;

      btn.innerHTML = 'Adding...';
      btn.disabled = true;

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(variantId), quantity: parseInt(quantity) }] })
      })
      .then(function(r) { return r.json(); })
      .then(function(data) {
        btn.innerHTML = '✓ Added to Cart!';
        btn.classList.add('cn-btn-add-to-cart--added');
        updateCartBubble();

        setTimeout(function() {
          btn.innerHTML = originalHTML;
          btn.classList.remove('cn-btn-add-to-cart--added');
          btn.disabled = false;
        }, 2000);
      })
      .catch(function(err) {
        console.error('Add to cart failed:', err);
        btn.innerHTML = 'Error — Try Again';
        setTimeout(function() {
          btn.innerHTML = originalHTML;
          btn.disabled = false;
        }, 2000);
      });
    });
  }

  /* ===== Sticky Bar — AJAX Add to Cart ===== */
  function initStickyBarAjax() {
    var bar = document.getElementById('cn-sticky-bar');
    if (!bar) return;

    var form = bar.querySelector('form');
    if (!form) return;

    form.addEventListener('submit', function(e) {
      e.preventDefault();

      var btn = form.querySelector('button[type="submit"]');
      var variantInput = form.querySelector('input[name="id"]');
      if (!btn || !variantInput) return;

      var originalHTML = btn.innerHTML;
      btn.innerHTML = '✓ Added!';
      btn.disabled = true;

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(variantInput.value), quantity: 1 }] })
      })
      .then(function(r) { return r.json(); })
      .then(function(data) {
        updateCartBubble();
        setTimeout(function() {
          btn.innerHTML = originalHTML;
          btn.disabled = false;
        }, 2000);
      })
      .catch(function(err) {
        console.error('Sticky bar add to cart failed:', err);
        btn.innerHTML = 'Error';
        setTimeout(function() {
          btn.innerHTML = originalHTML;
          btn.disabled = false;
        }, 2000);
      });
    });
  }

  /* ===== Bundle Add to Cart — Adds main product + free gift via AJAX ===== */
  function initBundleButton() {
    var bundleBtns = document.querySelectorAll('[data-cn-bundle-add]');
    bundleBtns.forEach(function(btn) {
      btn.addEventListener('click', function(e) {
        e.preventDefault();
        var mainVariantId = btn.getAttribute('data-variant-id');
        var bundleVariantId = btn.getAttribute('data-bundle-variant-id');
        
        if (!mainVariantId || !bundleVariantId) return;

        var originalHTML = btn.innerHTML;
        btn.innerHTML = 'Adding...';
        btn.disabled = true;

        // Add both items to cart via Shopify AJAX API
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [
              { id: parseInt(mainVariantId), quantity: 1 },
              { id: parseInt(bundleVariantId), quantity: 1 }
            ]
          })
        })
        .then(function(response) { return response.json(); })
        .then(function(data) {
          btn.innerHTML = '✓ Bundle Added!';
          btn.classList.add('cn-btn-add-to-cart--added');
          updateCartBubble();

          setTimeout(function() {
            btn.innerHTML = originalHTML;
            btn.classList.remove('cn-btn-add-to-cart--added');
            btn.disabled = false;
          }, 2000);
        })
        .catch(function(err) {
          console.error('Bundle add failed:', err);
          btn.innerHTML = 'Error — Try Again';
          setTimeout(function() {
            btn.innerHTML = originalHTML;
            btn.disabled = false;
          }, 2000);
        });
      });
    });
  }

  /* ===== Video Play ===== */
  function initVideoPlayer() {
    var wrappers = document.querySelectorAll('[data-cn-video]');
    wrappers.forEach(function(wrapper) {
      var playBtn = wrapper.querySelector('.cn-video__play-btn');
      if (!playBtn) return;

      playBtn.addEventListener('click', function() {
        var videoUrl = wrapper.getAttribute('data-video-url');
        if (videoUrl) {
          var iframe = document.createElement('iframe');
          iframe.src = videoUrl + (videoUrl.indexOf('?') > -1 ? '&' : '?') + 'autoplay=1';
          iframe.allow = 'autoplay; fullscreen; picture-in-picture';
          iframe.allowFullscreen = true;
          wrapper.appendChild(iframe);
          wrapper.classList.add('playing');
        }
      });
    });
  }

  /* ===== Initialize all modules ===== */
  function init() {
    initStickyBuyBar();
    initFadeIn();
    initFaqAccordion();
    initGallery();
    initCarousel();
    initVariantSelector();
    initAddToCart();
    initStickyBarAjax();
    initBundleButton();
    initVideoPlayer();
  }

  // Run on DOMContentLoaded or immediately if already loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    setTimeout(init, 50);
  }

  // Also run after full load to catch any Shopify async rendering
  window.addEventListener('load', function() {
    setTimeout(initStickyBuyBar, 200);
  });

})();


// ========== MOBILE FIXES (Feb 21, 2026) ==========

// Auto-generate YouTube thumbnails for video wrappers without posters
(function() {
  function getYouTubeId(url) {
    if (!url) return null;
    var match = url.match(/(?:embed\/|v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    return match ? match[1] : null;
  }

  // Set thumbnails on video wrappers that don't have poster images
  document.querySelectorAll('[data-cn-video]').forEach(function(wrapper) {
    var url = wrapper.getAttribute('data-video-url');
    var ytId = getYouTubeId(url);
    if (ytId && !wrapper.querySelector('.cn-video__poster')) {
      wrapper.setAttribute('data-yt-id', ytId);
      // Create poster from YouTube thumbnail
      var poster = document.createElement('img');
      poster.className = 'cn-video__poster';
      poster.src = 'https://img.youtube.com/vi/' + ytId + '/maxresdefault.jpg';
      poster.alt = 'Video thumbnail';
      poster.loading = 'lazy';
      poster.onerror = function() {
        this.src = 'https://img.youtube.com/vi/' + ytId + '/hqdefault.jpg';
      };
      wrapper.insertBefore(poster, wrapper.firstChild);
    }
  });

  // Also handle homepage video sections
  document.querySelectorAll('.hp-video__embed').forEach(function(embed) {
    var url = embed.getAttribute('data-video-url');
    var ytId = getYouTubeId(url);
    if (ytId && !embed.querySelector('.hp-video__poster-img')) {
      var poster = embed.querySelector('.hp-video__poster') || embed;
      var img = document.createElement('img');
      img.className = 'hp-video__poster-img';
      img.src = 'https://img.youtube.com/vi/' + ytId + '/maxresdefault.jpg';
      img.alt = 'Video thumbnail';
      img.loading = 'lazy';
      img.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;object-fit:cover;';
      img.onerror = function() {
        this.src = 'https://img.youtube.com/vi/' + ytId + '/hqdefault.jpg';
      };
      if (poster.tagName === 'DIV') {
        poster.insertBefore(img, poster.firstChild);
      }
    }
  });
})();

// Override video click handler to include rel=0 and modestbranding
(function() {
  // Product page videos (cn-video)
  document.querySelectorAll('[data-cn-video]').forEach(function(wrapper) {
    var playBtn = wrapper.querySelector('.cn-video__play-btn');
    if (!playBtn) return;
    
    // Remove old listener by cloning
    var newBtn = playBtn.cloneNode(true);
    playBtn.parentNode.replaceChild(newBtn, playBtn);
    
    newBtn.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      var videoUrl = wrapper.getAttribute('data-video-url');
      if (!videoUrl) return;
      var sep = videoUrl.indexOf('?') > -1 ? '&' : '?';
      var iframe = document.createElement('iframe');
      iframe.src = videoUrl + sep + 'autoplay=1&rel=0&modestbranding=1';
      iframe.allow = 'autoplay; fullscreen; picture-in-picture';
      iframe.allowFullscreen = true;
      iframe.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;border:0;border-radius:16px;z-index:10;';
      wrapper.classList.add('playing');
      wrapper.appendChild(iframe);
    });
    
    // Also make the whole wrapper clickable
    wrapper.style.cursor = 'pointer';
    wrapper.addEventListener('click', function(e) {
      if (e.target.closest('.cn-video__play-btn') || e.target.closest('iframe')) return;
      newBtn.click();
    });
  });
})();

// Fix: Color variant selector with proper image switching + full sync
(function() {
  var options = document.querySelectorAll('[data-cn-color-option]');
  var gallery = document.querySelector('[data-cn-gallery-main]');
  var colorName = document.querySelector('[data-cn-color-name]');
  var form = document.getElementById('cn-product-form');
  
  if (!options.length) return;
  
  options.forEach(function(opt) {
    opt.addEventListener('click', function() {
      // Update active state
      options.forEach(function(o) { o.classList.remove('active'); });
      opt.classList.add('active');
      
      // Update color name display
      if (colorName) {
        colorName.textContent = opt.getAttribute('data-color-name');
      }

      var variantId = opt.getAttribute('data-variant-id');
      
      // Update main product form hidden variant ID
      if (form) {
        var input = form.querySelector('input[name="id"]');
        if (input) {
          input.value = variantId;
        }
      }

      // Sync sticky bar variant
      var stickyInput = document.querySelector('#cn-sticky-bar input[name="id"]');
      if (stickyInput) {
        stickyInput.value = variantId;
      }

      // Sync bundle button variant
      var bundleBtn = document.querySelector('[data-cn-bundle-add]');
      if (bundleBtn) {
        bundleBtn.setAttribute('data-variant-id', variantId);
      }
      
      // Update gallery image if variant has image data
      var imgSrc = opt.getAttribute('data-variant-image');
      if (imgSrc && gallery) {
        var mainImg = gallery.querySelector('img');
        if (mainImg) {
          mainImg.src = imgSrc;
          mainImg.srcset = imgSrc.replace(/width=\d+/, 'width=400') + ' 400w, ' + 
                           imgSrc.replace(/width=\d+/, 'width=600') + ' 600w, ' +
                           imgSrc + ' 800w';
        }
      }
    });
  });
})();

// Fix: Menu drawer fallback — ensure it opens even if JS class fails
(function() {
  var drawer = document.querySelector('header-drawer');
  if (!drawer) return;
  
  var details = drawer.querySelector('details');
  if (!details) return;
  
  // Add a MutationObserver to ensure menu-opening class is added
  var observer = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      if (mutation.attributeName === 'open') {
        if (details.hasAttribute('open')) {
          // Force add menu-opening class after a small delay
          setTimeout(function() {
            if (!details.classList.contains('menu-opening')) {
              details.classList.add('menu-opening');
              document.body.classList.add('overflow-hidden-tablet');
            }
          }, 50);
        }
      }
    });
  });
  
  observer.observe(details, { attributes: true });
})();

